<template>
  <v-row class="d-flex align-center bi-parameter-rule-level-row">
    <v-col cols="auto" class="bi-parameter-rule-level-col">
      <Autocomplete
        :value="form.level"
        :items="levelOptions"
        item-text="label"
        item-value="code"
        :label="$t('biControlPanel.parameterRules.wizard.levelType')"
        :rules="[rule.required]"
        :disabled="!isStep1Editable"
        hide-details
        v-bind="autocompleteAttrs"
        @change="setLevel"
      />
    </v-col>

    <v-col v-if="form.level === 'CARRIER'" cols="auto" class="bi-parameter-rule-level-col">
      <PaginatedAutocomplete
        :value="form.carrierId"
        :fetch="fetchCarriers"
        :prepend-items="() => editScopeItems('carrierId')"
        item-text="name"
        item-value="id"
        :label="$t('biControlPanel.parameterRules.wizard.carrier')"
        :rules="[rule.required]"
        :disabled="!isStep1Editable"
        :min-search-chars="0"
        hide-details
        v-bind="autocompleteAttrs"
        @input="setScope('carrierId', $event)"
        @error="handleError"
      />
    </v-col>

    <v-col v-if="showFinancialGroupField" cols="auto" class="bi-parameter-rule-level-col">
      <PaginatedAutocomplete
        :value="form.financialGroupId"
        :fetch="fetchFinancialGroups"
        :prepend-items="() => editScopeItems('financialGroupId')"
        item-text="name"
        item-value="id"
        :label="$t('biControlPanel.parameterRules.wizard.financialGroup')"
        :rules="[rule.required]"
        :disabled="!isStep1Editable"
        :min-search-chars="0"
        hide-details
        v-bind="autocompleteAttrs"
        @input="setScope('financialGroupId', $event)"
        @error="handleError"
      />
    </v-col>

    <v-col v-if="form.level === 'CONTRACT'" cols="auto" class="bi-parameter-rule-level-col">
      <PaginatedAutocomplete
        :value="form.contractId"
        :fetch="fetchContracts"
        :prepend-items="() => editScopeItems('contractId')"
        item-text="name"
        item-value="id"
        :label="$t('biControlPanel.parameterRules.wizard.contract')"
        :rules="[rule.required]"
        :disabled="!isStep1Editable || !form.financialGroupId"
        :min-search-chars="0"
        hide-details
        v-bind="autocompleteAttrs"
        @input="setScope('contractId', $event)"
        @error="handleError"
      />
    </v-col>

    <v-col cols="auto" class="bi-parameter-rule-level-col">
      <Autocomplete
        :value="form.paymentModels"
        :items="paymentModelOptions"
        item-text="label"
        item-value="code"
        multiple
        :label="$t('biControlPanel.parameterRules.wizard.paymentModels')"
        :rules="[rule.requiredArray]"
        :disabled="!isStep1Editable"
        hide-details
        v-bind="autocompleteAttrs"
        @change="setPaymentModels"
      >
        <template #selection="{ item, index }">
          <v-chip
            v-if="index === 0"
            small
            :close="isStep1Editable"
            color="primary"
            class="bi-compact-chip"
            :class="{ 'bi-compact-chip--grouped': form.paymentModels.length > 1 }"
            @click:close="setPaymentModels(form.paymentModels.filter((code) => code !== item.code))"
          >
            <span class="bi-compact-chip__text">{{ item.label }}</span>
          </v-chip>
          <v-chip
            v-else-if="index === 1"
            small
            :close="isStep1Editable"
            color="primary"
            @click:close="setPaymentModels(form.paymentModels.slice(0, 1))"
          >
            +{{ form.paymentModels.length - 1 }}
          </v-chip>
        </template>
      </Autocomplete>
    </v-col>

    <v-spacer />

    <v-col v-if="isStep1Editable" cols="auto" class="d-flex" style="gap: 12px;">
      <v-btn outlined color="primary" height="42" @click="$emit('cancel')">
        {{ $t('biControlPanel.parameterRules.wizard.cancel') }}
      </v-btn>
      <v-btn color="primary" elevation="0" height="42" @click="$emit('advance')">
        {{ $t('biControlPanel.parameterRules.wizard.advance') }}
      </v-btn>
    </v-col>

    <SnackbarCustomize ref="SnackbarCustomize" />
  </v-row>
</template>

<script>
import Autocomplete from '@/components/Default/Autocomplete.vue';
import PaginatedAutocomplete from '@/components/Default/PaginatedAutocomplete.vue';
import SnackbarCustomize from '@/components/CustomAlerts/SnackbarCustomize.vue';
import { AUTOCOMPLETE_ATTRS } from '@/shared/constants/admissionMap/DefaultConstants';
import BiPanelRuleModule from '@/store/modules/bi-panel-rule/bi-panel-rule-module';
import BiPanelRuleService from '@/services/bi-panel-rule-service';
import Rules from '@/shared/validators/formRules';
import {
  BI_PANEL_LEVEL_OPTIONS,
  BI_PAYMENT_MODEL_OPTIONS,
} from '@/store/modules/bi-panel-rule/bi-panel-rule-factory';

const biPanelRuleService = new BiPanelRuleService();

export default {
  name: 'LevelSelectionStep',
  components: { Autocomplete, PaginatedAutocomplete, SnackbarCustomize },
  data() {
    return {
      levelOptions: BI_PANEL_LEVEL_OPTIONS,
      paymentModelOptions: BI_PAYMENT_MODEL_OPTIONS,
      rule: new Rules(),
    };
  },
  computed: {
    form() {
      return BiPanelRuleModule.form;
    },
    isStep1Editable() {
      // Na edição o BE não aceita alterar nível, escopo nem modelo; liberar aqui descartaria a mudança ao salvar
      return this.form.step === 1 && this.form.mode === 'create';
    },
    showFinancialGroupField() {
      if (this.form.level === 'FINANCIAL_GROUP') return true;

      // Em CONTRACT o Grupo Financeiro só filtra o combo de Contrato; a regra salva não guarda esse id
      return this.form.level === 'CONTRACT' && this.form.mode === 'create';
    },
    autocompleteAttrs() {
      return {
        ...AUTOCOMPLETE_ATTRS,
        borderSelected: false,
        placeholder: this.translate('biControlPanel.filters.placeholder'),
      };
    },
  },
  methods: {
    editScopeItems(field) {
      if (this.form.mode !== 'edit' || !this.form[field]) return [];

      return [{ id: this.form[field], name: this.form.scopeLabel || String(this.form[field]) }];
    },
    setLevel(level) {
      BiPanelRuleModule.setFormLevel(level || null);
    },
    setScope(field, value) {
      BiPanelRuleModule.setFormScopeValue({ field, value: value || null });
    },
    setPaymentModels(paymentModels) {
      BiPanelRuleModule.setFormPaymentModels(paymentModels || []);
    },
    fetchCarriers(page, size, searchText) {
      return this.fetchOptions(() => biPanelRuleService.fetchCarrierOptions(page, size, searchText));
    },
    fetchFinancialGroups(page, size, searchText) {
      return this.fetchOptions(() => biPanelRuleService.fetchFinancialGroupOptions(page, size, searchText));
    },
    fetchContracts(page, size, searchText) {
      if (!this.form.financialGroupId) {
        return Promise.resolve({ content: [], last: true });
      }

      return this.fetchOptions(
        () => biPanelRuleService.fetchContractOptions(page, size, searchText, this.form.financialGroupId),
      );
    },
    async fetchOptions(request) {
      try {
        const response = await request();

        return response.data;
      } catch (error) {
        this.handleError();

        return { content: [], last: true };
      }
    },
    handleError() {
      this.$refs.SnackbarCustomize.open('error', this.translate('biControlPanel.parameterRules.messages.filterOptionsLoadError'));
    },
    translate(key) {
      return this.$t(key).toString();
    },
  },
};
</script>

<style lang="scss" scoped>
.bi-parameter-rule-level-row {
  margin: 0;
  row-gap: 12px;
}

.bi-parameter-rule-level-col {
  min-width: 200px;
  max-width: 260px;
  padding: 4px 8px;
}
</style>
